import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import Archive from './components/Archive';
import ImageUpload from './components/ImageUpload';
import BulkGridEditor from './components/BulkGridEditor';
import Reports from './components/Reports';
import Settings from './components/Settings';
import BuildingManagement from './components/BuildingManagement';
import BuildingInfo from './components/BuildingInfo';
import Login from './components/Login';
import apiService from './services/api';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});

function App() {
  const [folderStructure, setFolderStructure] = useState({});
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'elevations', or 'editor'
  const [currentProject, setCurrentProject] = useState(null); // Store current project info
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const notificationSeqRef = useRef(0);
  const notificationsRef = useRef(notifications);
  const reportJobSyncErrorCountsRef = useRef({});

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('user');
        
        if (token && userData) {
          // Verify token is still valid with backend
          await apiService.verifyToken();
          const currentUser = JSON.parse(userData);
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          // No token or user data found
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        // Token is invalid or expired, clear auth state
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Support deep-linking and browser navigation (back/forward buttons)
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    // Handle initial load from URL
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    const buildingIdParam = params.get('buildingId');
    const projectIdParam = params.get('projectId');
    const projectNameParam = params.get('projectName');
    const imageTypeParam = params.get('imageType') || params.get('image_type') || params.get('initialImageType');
    
    let initialProject = null;
    let initialView = 'dashboard';

    // If buildingId is provided, set it in currentProject
    if (buildingIdParam) {
      const parsedBuildingId = parseInt(buildingIdParam, 10);
      if (!Number.isNaN(parsedBuildingId)) {
        let resolvedImageType = imageTypeParam || null;
        if (!resolvedImageType) {
          try {
            const stored = localStorage.getItem(`last_image_type_building_${parsedBuildingId}`);
            if (stored) resolvedImageType = stored;
          } catch {}
        }
        initialProject = {
          buildingId: parsedBuildingId,
          projectId: projectIdParam ? parseInt(projectIdParam, 10) : undefined,
          projectName: projectNameParam,
          initialImageType: resolvedImageType || null,
        };
        setCurrentProject(initialProject);
      }
    } else if (projectIdParam) {
       // Just project context
       let resolvedImageType = imageTypeParam || null;
       initialProject = {
          projectId: parseInt(projectIdParam, 10),
          projectName: projectNameParam,
          ...(resolvedImageType ? { initialImageType: resolvedImageType } : {}),
       };
       setCurrentProject(initialProject);
    }

    // Navigate to requested view, default to editor when buildingId is present
    if (viewParam) {
      initialView = viewParam;
    } else if (buildingIdParam) {
      initialView = 'editor';
    }
    
    setCurrentView(initialView);
    
    // Replace current history entry with correct state so back button works later
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({ view: initialView, project: initialProject }, '', newUrl);

    // Handle popstate events (back/forward button)
    const handlePopState = (event) => {
      if (event.state) {
        setCurrentView(event.state.view || 'dashboard');
        if (event.state.project) {
          setCurrentProject(event.state.project);
        } else {
          setCurrentProject(null);
        }
      } else {
        // Fallback for when state is missing but URL has params
        const currentParams = new URLSearchParams(window.location.search);
        const currentViewParam = currentParams.get('view') || 'dashboard';
        setCurrentView(currentViewParam);
        // We could parse project params here too if needed, but state is safer
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isLoading, isAuthenticated]);

  // Helper to update state and push history
  const navigateTo = (view, projectData = null) => {
    // Prevent navigation to login if user is authenticated
    if (view === 'login' && isAuthenticated) {
      return;
    }

    let nextProject = projectData ? { ...currentProject, ...projectData } : currentProject;
    if (projectData && Object.prototype.hasOwnProperty.call(projectData, 'projectId') && !Object.prototype.hasOwnProperty.call(projectData, 'buildingId')) {
      nextProject = {
        ...nextProject,
        buildingId: null,
        buildingName: null,
      };
    }

    if ((view === 'elevations' || view === 'editor' || view === 'reports') && (!nextProject?.projectId || !nextProject?.buildingId)) {
      return;
    }
    
    setCurrentView(view);
    if (projectData) {
      setCurrentProject(nextProject);
    }

    // Construct URL params
    const params = new URLSearchParams();
    if (view && view !== 'dashboard') params.set('view', view);
    
    if (nextProject) {
      if (nextProject.projectId) params.set('projectId', nextProject.projectId);
      if (nextProject.projectName) params.set('projectName', nextProject.projectName);
      if (nextProject.buildingId) params.set('buildingId', nextProject.buildingId);
      if (nextProject.initialImageType) params.set('imageType', nextProject.initialImageType);
    }

    try {
      if (nextProject?.buildingId && nextProject?.initialImageType) {
        localStorage.setItem(`last_image_type_building_${nextProject.buildingId}`, nextProject.initialImageType);
      }
    } catch {}

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ view, project: nextProject }, '', newUrl);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    // Preserve current URL (including query params) so deep-links like ?view=editor&buildingId=... work after login
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('view') === 'login') {
        params.delete('view');
        const next = params.toString();
        const newUrl = next ? `${window.location.pathname}?${next}` : window.location.pathname;
        window.history.replaceState(null, '', newUrl);
      }
    } catch {}
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setNotifications([]);
      setCurrentView('dashboard');
      window.history.pushState(null, '', window.location.pathname);
    }
  };
  
  const handleFolderUpload = (newFolderStructure, buildingId = null, imageType = null, buildingName = null) => {
    setFolderStructure(newFolderStructure);
    // Preserve selected building context for subsequent navigation
    const newProjectData = { 
      buildingId,
      ...(buildingName ? { buildingName } : {}),
      initialImageType: imageType || currentProject?.initialImageType
    };
    navigateTo('editor', newProjectData);
  };
  
  const handleBackToElevations = () => {
    navigateTo('elevations');
  };
  
  const handleNavigate = (view, projectData = null) => {
    navigateTo(view, projectData);
  };

  const handleBackNavigation = () => {
    // Smart back navigation that doesn't go to login if authenticated
    if (isAuthenticated) {
      // If we have history, we could use history.back(), but for predictable app behavior 
      // in this specific "Back" button context, we often want to go "up" a level.
      // However, the user asked for browser back button behavior.
      // The onBack prop in components usually means "go up" or "cancel".
      // Let's keep the existing logic but use navigateTo to update history.
      navigateTo('dashboard');
    } else {
      setCurrentView('login');
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;
    const loadNotifications = async () => {
      try {
        const items = await apiService.getNotifications(50);
        if (cancelled || !Array.isArray(items)) return;
        setNotifications(items);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };

    loadNotifications();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const persistNotification = (notification) => {
    if (!notification || !apiService.isAuthenticated()) return;
    apiService.saveNotification(notification).catch((error) => {
      console.error('Failed to save notification:', error);
    });
  };

  const upsertNotification = (notification) => {
    if (!notification) return null;

    if (!notification.id) {
      notificationSeqRef.current += 1;
    }
    const nextId = notification.id || `notification-${Date.now()}-${notificationSeqRef.current}`;
    const now = new Date().toISOString();
    const existingItem = notificationsRef.current.find((item) => item.id === nextId) || null;
    const nextItem = {
      id: nextId,
      type: notification.type || existingItem?.type || 'info',
      title: notification.title || existingItem?.title || 'Notification',
      message: notification.message ?? existingItem?.message ?? '',
      status: notification.status || existingItem?.status || 'info',
      progress: typeof notification.progress === 'number'
        ? notification.progress
        : (typeof existingItem?.progress === 'number' ? existingItem.progress : 0),
      read: typeof notification.read === 'boolean' ? notification.read : Boolean(existingItem?.read),
      projectName: notification.projectName || existingItem?.projectName || '',
      buildingName: notification.buildingName || existingItem?.buildingName || '',
      filename: notification.filename || existingItem?.filename || '',
      reportName: notification.reportName || existingItem?.reportName || '',
      file_url: notification.file_url || existingItem?.file_url || '',
      jobId: notification.jobId || existingItem?.jobId || '',
      createdAt: notification.createdAt || existingItem?.createdAt || now,
      updatedAt: now,
    };

    setNotifications((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === nextId);
      if (existingIndex >= 0) {
        const nextList = [...prev];
        nextList[existingIndex] = {
          ...nextList[existingIndex],
          ...nextItem,
          createdAt: nextList[existingIndex].createdAt || nextItem.createdAt,
        };
        return nextList;
      }

      return [nextItem, ...prev].slice(0, 50);
    });
    persistNotification(nextItem);

    return nextId;
  };

  const getReportNameFromFileUrl = (fileUrl = '') => {
    if (!fileUrl) return '';
    const cleaned = String(fileUrl).split('?')[0].split('#')[0];
    const parts = cleaned.split('/').filter(Boolean);
    const filename = parts[parts.length - 1] || '';
    return filename.replace(/\.[^.]+$/, '');
  };

  const markAllNotificationsRead = () => {
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    if (apiService.isAuthenticated()) {
      apiService.markAllNotificationsRead().catch((error) => {
        console.error('Failed to mark notifications read:', error);
      });
    }
  };

  const markNotificationRead = (notificationId, notification = null) => {
    if (!notificationId) return;
    setNotifications((prev) =>
      prev.map((item) => (item.id === notificationId ? { ...item, read: true } : item))
    );
    if (apiService.isAuthenticated()) {
      apiService.markNotificationRead(notificationId).catch((error) => {
        console.error('Failed to mark notification read:', error);
      });
    }

    if (
      notification &&
      notification.type === 'report' &&
      notification.status === 'completed' &&
      notification.file_url
    ) {
      const normalizedPath = String(notification.file_url).trim();
      if (normalizedPath) {
        try {
          const absoluteUrl = new URL(normalizedPath, apiService.baseURL).toString();
          window.open(absoluteUrl, '_blank');
        } catch (error) {
          console.error('Failed to open report notification file:', error);
        }
      }
    }
  };

  const clearNotifications = () => {
    setNotifications([]);
    if (apiService.isAuthenticated()) {
      apiService.clearNotifications().catch((error) => {
        console.error('Failed to clear notifications:', error);
      });
    }
  };

  const activeReportJobIds = [...new Set(
    notifications
      .filter((item) => item.type === 'report' && item.jobId && (item.status === 'queued' || item.status === 'running'))
      .map((item) => String(item.jobId))
  )].sort().join('|');

  useEffect(() => {
    if (!activeReportJobIds) return undefined;

    let cancelled = false;
    const jobIdList = activeReportJobIds.split('|').filter(Boolean);

    const syncReportJobs = async () => {
      try {
        const jobs = await Promise.all(jobIdList.map((jobId) => apiService.getReportJob(jobId).catch((error) => ({ error, jobId }))));
        if (cancelled) return;

        jobs.forEach((job) => {
          if (!job || job.error || !job.job_id) {
            const failedJobId = String(job?.jobId || '');
            if (!failedJobId) return;
            const existing = notificationsRef.current.find((item) => String(item.jobId || '') === failedJobId);
            if (!existing) return;

            const nextErrorCount = (reportJobSyncErrorCountsRef.current[failedJobId] || 0) + 1;
            reportJobSyncErrorCountsRef.current[failedJobId] = nextErrorCount;
            const isMissingJob = Number(job?.error?.status) === 404;
            const isDocxJob = String(existing.message || '').toUpperCase().includes('DOCX');
            const maxErrors = isDocxJob ? 24 : 3;
            if (!isMissingJob && nextErrorCount < maxErrors) return;

            delete reportJobSyncErrorCountsRef.current[failedJobId];
            upsertNotification({
              id: existing.id,
              type: 'report',
              title: 'Report failed',
              message: isMissingJob
                ? 'Report job status is no longer available. Please generate the report again.'
                : 'Unable to reach the report job status endpoint. Please try generating the report again.',
              status: 'failed',
              progress: 0,
              read: false,
              projectName: existing.projectName,
              buildingName: existing.buildingName,
              filename: existing.filename,
              reportName: existing.reportName,
              file_url: existing.file_url,
              jobId: failedJobId,
              createdAt: existing.createdAt,
            });
            return;
          }
          delete reportJobSyncErrorCountsRef.current[String(job.job_id)];
          const existing = notificationsRef.current.find((item) => String(item.jobId || '') === String(job.job_id));
          if (!existing) return;

          const nextStatus = job.status || existing.status || 'running';
          const nextFileUrl = job.file_url || existing.file_url || '';
          const nextMessage =
            job.message ||
            (nextStatus === 'completed'
              ? 'The report is ready.'
              : nextStatus === 'failed'
                ? 'Report generation failed.'
                : existing.message || 'Generating report...');

          upsertNotification({
            id: existing.id,
            type: 'report',
            title: nextStatus === 'completed' ? 'Report ready' : nextStatus === 'failed' ? 'Report failed' : 'Generating report',
            message: nextMessage,
            status: nextStatus,
            progress:
              nextStatus === 'completed'
                ? 100
                : nextStatus === 'failed'
                  ? 0
                  : existing.progress || 10,
            read: existing.read,
            projectName: existing.projectName,
            buildingName: existing.buildingName,
            filename: existing.filename || (nextStatus === 'completed' ? getReportNameFromFileUrl(nextFileUrl) : ''),
            reportName: existing.reportName || getReportNameFromFileUrl(nextFileUrl),
            file_url: nextFileUrl,
            jobId: job.job_id,
            createdAt: existing.createdAt,
          });
        });
      } catch (error) {
        console.error('Failed to sync report jobs:', error);
      }
    };

    syncReportJobs();
    const timer = window.setInterval(syncReportJobs, 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeReportJobIds]);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="app">
        {isLoading ? (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="100vh"
          >
            <CircularProgress size={60} />
          </Box>
        ) : !isAuthenticated ? (
          <Login onLogin={handleLogin} />
        ) : (
          <>
            <Header 
              currentView={currentView}
              currentProject={currentProject}
              onNavigate={handleNavigate}
              onBack={handleBackNavigation}
              user={user}
              onLogout={handleLogout}
              notifications={notifications}
              onNotificationOpen={markAllNotificationsRead}
              onNotificationRead={markNotificationRead}
              onNotificationsClear={clearNotifications}
            />
            <main className="main-content">
              {currentView === 'dashboard' && (
                <Dashboard onNavigate={handleNavigate} />
              )}
              {currentView === 'archive' && (
                <Archive onNavigate={handleNavigate} />
              )}
              {currentView === 'elevations' && (
                <ImageUpload 
                  onFolderStructureChange={handleFolderUpload} 
                  onNavigate={handleNavigate}
                  onBack={handleBackNavigation}
                  projectId={currentProject?.projectId}
                  projectName={currentProject?.projectName}
                  buildingId={currentProject?.buildingId}
                  initialImageType={currentProject?.initialImageType || null}
                />
              )}
              {currentView === 'editor' && (
                <BulkGridEditor 
                  folderStructure={folderStructure} 
                  onBack={handleBackNavigation}
                  onNavigate={handleNavigate}
                  projectId={currentProject?.projectId}
                  projectName={currentProject?.projectName}
                  buildingId={currentProject?.buildingId}
                  buildingName={currentProject?.buildingName}
                  imageType={currentProject?.initialImageType || null}
                />
              )}
              {currentView === 'reports' && (
                <Reports 
                  projectId={currentProject?.projectId}
                  projectName={currentProject?.projectName}
                  buildingId={currentProject?.buildingId}
                  imageType={currentProject?.initialImageType || 'drone'}
                  onBack={handleBackNavigation}
                  onNotificationUpsert={upsertNotification}
                />
              )}
              {currentView === 'settings' && (
                <Settings />
              )}
              {currentView === 'buildings' && (
                <BuildingManagement 
                  projectId={currentProject?.projectId}
                  projectName={currentProject?.projectName}
                  onBack={handleBackNavigation}
                  onNavigate={handleNavigate}
                />
              )}
              {currentView === 'buildingInfo' && (
                <BuildingInfo 
                  buildingId={currentProject?.buildingId}
                  projectId={currentProject?.projectId}
                  projectName={currentProject?.projectName}
                  onBack={handleBackNavigation}
                />
              )}
              
            </main>
          </>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
